import React from "react"
import CompoundChurnInput from "./compoundChurnInput"
import { Row, Col, Table, Popover, OverlayTrigger } from "react-bootstrap"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { formatCurrency } from './utils'

import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'

const ModelDetailsTable = (props) => {
  const {mrrGrowth, lostCurrent, lostTarget, currentChurn, targetChurn} = props

  const popover = (id, churn, loss) => {
    const buildExplanationItems = (nMonth, churn) => {
      let items = []
      for (let step = nMonth; step > 0; step--) {
        items.push(
          <div>
            {`${formatCurrency(mrrGrowth[step - 1])} x ${churn}% x ${nMonth - step + 1} months`}
          </div>
        )
      }

      return items
    }

    return (
      <Popover id={id}>
        <Popover.Title as="h3">Month {id+1} loss</Popover.Title>
        <Popover.Content>
          {buildExplanationItems(id + 1, churn)}
          <hr />
          <div>Total sum:<strong>&nbsp;{formatCurrency(loss[id])}</strong></div>
        </Popover.Content>
      </Popover>
    )
  }

  const totalLoss = lostCurrent[lostCurrent.length - 1]
  const saving = totalLoss - lostTarget[lostTarget.length - 1]

  return (<>
    {lostCurrent.length > 0 && (<p>Total compound loss over 6 month is <b><big>{formatCurrency(totalLoss)}</big></b>. If you reduced churn to {targetChurn}% you would save <big><b>{formatCurrency(saving)}</b></big></p>)}
    <Table responsive>
      <thead>
        <tr>
          <th></th>
          {mrrGrowth.map((value, i) =>
            <th>{`Month ${i+1}`}</th>
           )}
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th>MRR</th>
          {mrrGrowth.map((value, i) =>
            <td>
              {formatCurrency(value)}
            </td>
           )}
        </tr>
        <tr>
          <th>Compound loss from {currentChurn}% churn</th>
          {lostCurrent.map((value, i) =>
            <td>
              {formatCurrency(value)}
              <span>&nbsp;</span>
              <OverlayTrigger trigger="hover" placement="left" overlay={popover(i, currentChurn, lostCurrent)}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </OverlayTrigger>
            </td>
           )}
        </tr>
        <tr>
          <th>Compound loss from {targetChurn}% churn</th>
          {lostTarget.map((value, i) =>
            <td>
              {formatCurrency(value)}
              <span>&nbsp;</span>
              <OverlayTrigger trigger="hover" placement="left" overlay={popover(i, targetChurn, lostTarget)}>
                <FontAwesomeIcon icon={faInfoCircle}/>
              </OverlayTrigger>
            </td>
           )}
        </tr>
      </tbody>
    </Table>
  </>)
}

const ImpactChart = (props) => {
  const {data, currentChurn, targetChurn} = props

  const formateer = (value, name, props) => {
    return [formatCurrency(value), name]
  }

  const labelFormatter = (label) => {
    return `Compound loss ${label}`
  }

  return (
    <ResponsiveContainer width="90%" height={400} margin={{ top: 20, bottom: 20 }}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" padding={{ left: 30, right: 30 }} />
        <YAxis />
        <Tooltip formatter={formateer} labelFormatter={labelFormatter}/>
        <Legend />
        <Line name={`Churn ${currentChurn}%`} type="monotone" dataKey="lostCurrent" stroke="#8884d8" activeDot={{ r: 6 }} />
        <Line name={`Churn ${targetChurn}%`}  type="monotone" dataKey="lostTarget" stroke="#82ca9d" activeDot={{ r: 6 }}/>
      </LineChart>
    </ResponsiveContainer>
  )
}

class CompoundChurnImpact extends React.Component {
  constructor(props) {
    super(props)
    this.mrrGrowth = []
    this.state = {
      lostCurrent: [],
      lostTarget: []
    }
  }

  componentDidMount = () => {
    this.calculateImpact(
      {
        startingMrr: 300000,
        mrrGrowth: 10,
        currentChurn: 5,
        targetChurn: 2.5
      }
    )
  }

  calculateImpact = (data) => {
    const numOfMonth = 6
  
    const {startingMrr, mrrGrowth, currentChurn, targetChurn} = data
    this.currentChurn = currentChurn
    this.targetChurn = targetChurn
  
    let mrr = [startingMrr]
    const growthMultiplier = mrrGrowth / 100
    for (let i = 0; i < numOfMonth - 1; i++) {
      mrr.push(mrr[i] + mrr[i] * growthMultiplier)
    }

    this.mrrGrowth = mrr
  
    let lostCurrent = [0, 0, 0, 0, 0, 0]
    let lostTarget = [0, 0, 0, 0, 0, 0]
    for (let step = 0; step < numOfMonth; step++) {
      let alreadyLost = 0
      let targetLost = 0
      for (let j = 0; j < step; j++) {
        alreadyLost = alreadyLost + mrr[j] * (currentChurn / 100) * (step - j + 1)
        targetLost = targetLost + mrr[j] * (targetChurn / 100) * (step - j + 1)
      }
  
      lostCurrent[step] = Math.round(alreadyLost + mrr[step] * (currentChurn / 100))
      lostTarget[step] = Math.round(targetLost + mrr[step] * (targetChurn / 100))
    }
  
    this.setState({lostCurrent, lostTarget})
  }

  convertToLineChartData = (lostCurrent, lostTarget) => {
    let data = []
    for (let step = 0; step < 6; step++) {
      data.push(
        {
          month: `Month ${step + 1}`,
          lostCurrent: lostCurrent[step],
          lostTarget: lostTarget[step]
        }
      )
    }

    return data
  }


  render() {
    const {lostCurrent, lostTarget} = this.state

    return (<>
      <Row className="justify-content-md-center mb-5">
        <Col className="justify-content-md-center">
          <h1 className='text-center mb-3'><small>Is my monthly churn a problem?</small></h1>
          <p class='lead'>Churn in SaaS has compund effect. This model tells your how much money you lose over 6 month with you current churn and what would be the loss if you reduce your churn.</p>
        </Col>
      </Row>
      <Row className="justify-content-md-center">
        <Col className="justify-content-md-center sm-mb-5" sm='12' md='12' lg='5'>
          <CompoundChurnInput onSubmit={this.calculateImpact}/>
        </Col>
        <Col sm='12' md='12' lg='7'>
          <ImpactChart data={this.convertToLineChartData(lostCurrent, lostTarget)} currentChurn={this.currentChurn} targetChurn={this.targetChurn}/>
        </Col>
      </Row>
      <Row className="justify-content-md-center mt-5">
        <Col>
          <ModelDetailsTable currentChurn={this.currentChurn} targetChurn={this.targetChurn} mrrGrowth={this.mrrGrowth} lostCurrent={lostCurrent} lostTarget={lostTarget}/>
        </Col>
      </Row>
    </>
    )
  }
}

export default CompoundChurnImpact
